import { useEffect, useRef } from "react";
import { http } from "@/api";
import { tokenStorage } from "@/core/services/tokenStorage";

/**
 * Builds the base WebSocket URL matching the current environment protocol and host.
 */
export function getInboxWebSocketBaseUrl() {
  const apiURL =
    import.meta.env.DEV && import.meta.env.VITE_USE_DIRECT_API !== "true"
      ? ""
      : import.meta.env.VITE_API_URL || "";
  const protocol =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "wss:"
      : "ws:";
  const host =
    typeof window !== "undefined" && window.location.host
      ? window.location.host
      : "localhost";

  return apiURL
    ? `${apiURL.replace(/^http/, "ws").replace(/\/$/, "")}/api/inbox/ws`
    : `${protocol}//${host}/api/inbox/ws`;
}

const FATAL_AUTH_CODES = new Set([1008, 4001, 4401]);

/**
 * Calculates exponential backoff delay with randomized jitter.
 * Implements Equal Jitter (half-jitter): delay is uniformly distributed
 * within [0.5 * maxBackoff, maxBackoff], where maxBackoff = min(30000, 1000 * 2^retryCount).
 *
 * @param {number} retryCount - Zero-indexed consecutive retry count
 * @param {Function} [randomFn=Math.random] - RNG function (injectable for deterministic unit testing)
 * @returns {number} Backoff delay in milliseconds (integer)
 */
export function calculateReconnectDelay(retryCount, randomFn = Math.random) {
  const safeRetry = Math.min(Math.max(0, Number(retryCount) || 0), 30);
  const maxBackoff = Math.min(1000 * Math.pow(2, safeRetry), 30000);
  return Math.floor(maxBackoff * (0.5 + randomFn() * 0.5));
}

/**
 * Hook to manage the inbox WebSocket connection lifecycle with single-use ticket handshake,
 * automatic backoff reconnection, offline awareness, and Policy Violation (1008) guard.
 *
 * @param {Object} options
 * @param {Object|null} options.user Current authenticated user
 * @param {Function} options.onNotification Callback when NEW_ITEM notification message is received
 */
export function useInboxWebSocket({ user, onNotification }) {
  const userId = user?.id;
  const organizationId = user?.organizationId || user?.organization_id;
  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;

  useEffect(() => {
    if (!userId || !organizationId) return;

    let socket = null;
    let reconnectTimeout = null;
    let abortController = null;
    let isMounted = true;
    let isConnecting = false;
    let retryCount = 0;

    const wsBaseUrl = getInboxWebSocketBaseUrl();

    async function acquireTicketAndConnect() {
      if (!isMounted || isConnecting) return;

      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        return;
      }

      isConnecting = true;
      abortController = new AbortController();

      let wsUrl = null;

      try {
        const response = await http.post(
          "/api/inbox/ws-ticket",
          {},
          { signal: abortController.signal }
        );

        if (!isMounted) return;

        const ticket = response?.data?.ticket;
        if (ticket) {
          wsUrl = `${wsBaseUrl}?ticket=${encodeURIComponent(ticket)}`;
        }
      } catch (err) {
        if (!isMounted) return;

        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          isConnecting = false;
          return;
        }

        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          console.warn("[Inbox WebSocket] Authentication failed on ticket request, halting reconnect.");
          isConnecting = false;
          return;
        }

        if (status === 404) {
          const fallbackToken = tokenStorage.getAccessToken();
          if (fallbackToken) {
            console.warn(
              "[Inbox WebSocket] /api/inbox/ws-ticket 404 Not Found. Falling back to legacy token param."
            );
            wsUrl = `${wsBaseUrl}?token=${encodeURIComponent(fallbackToken)}`;
          } else {
            wsUrl = wsBaseUrl;
          }
        }

        if (!wsUrl) {
          isConnecting = false;
          scheduleReconnect();
          return;
        }
      }

      if (!isMounted || !wsUrl) {
        isConnecting = false;
        return;
      }

      try {
        socket = new WebSocket(wsUrl);
      } catch (wsErr) {
        console.error("[Inbox WebSocket] Failed to instantiate WebSocket:", wsErr);
        isConnecting = false;
        scheduleReconnect();
        return;
      }

      socket.onopen = () => {
        isConnecting = false;
        retryCount = 0;
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "NEW_ITEM" && data.item) {
            onNotificationRef.current?.(data.item);
          }
        } catch (err) {
          console.error("[Inbox WebSocket] Failed to parse message:", err);
        }
      };

      socket.onclose = (event) => {
        isConnecting = false;
        if (!isMounted || event.code === 1000 || FATAL_AUTH_CODES.has(event.code)) {
          return;
        }
        scheduleReconnect();
      };

      socket.onerror = () => {
        // Let onclose handle reconnect logic
      };
    }

    function scheduleReconnect() {
      if (!isMounted) return;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);

      const delay = calculateReconnectDelay(retryCount);
      retryCount++;

      reconnectTimeout = setTimeout(() => {
        acquireTicketAndConnect();
      }, delay);
    }

    function handleOnline() {
      if (!isMounted) return;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      retryCount = 0;
      acquireTicketAndConnect();
    }

    function handleOffline() {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
      }
    }

    if (typeof window !== "undefined") {
      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);
    }

    acquireTicketAndConnect();

    return () => {
      isMounted = false;
      if (typeof window !== "undefined") {
        window.removeEventListener("online", handleOnline);
        window.removeEventListener("offline", handleOffline);
      }

      if (abortController) {
        abortController.abort();
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (socket) {
        socket.onopen = null;
        socket.onclose = null;
        socket.onerror = null;
        socket.onmessage = null;
        if (typeof socket.close === "function") {
          if (socket.readyState === 0) {
            socket.onopen = () => {
              try {
                socket.close();
              } catch {
                // Ignore if already closing
              }
            };
          } else {
            try {
              socket.close();
            } catch {
              // Ignore if already closed
            }
          }
        }
      }
    };
  }, [userId, organizationId]);
}
